import React, {useCallback, useEffect, useState} from "react";
import {useGlobal} from "../../Store";
import {Link} from "react-router-dom";
import {fetchData, postData} from "../../Utils/Server";
import Endpoints from "../../Constants/Endpoints";
import {Guard} from "../Common/Guard";
import {PERMS} from "../../Utils/Permissions";
import {useHistory} from "react-router-dom";
import {ERROR, Notify, SUCCESS} from "../../Utils/Notify";
import {updateDocumentTitle} from "../../Utils/Common";
import AddRatesComponent from "../Views/AddRatesComponent";
import ListRatesComponent from "../Views/ListRatesComponent";
import styled from "styled-components";
import PageLayout from "../Layout/PageLayout";
import {
    Button, Card, CardTitleBar, CardBody, Tabs, DataTable,
    FormField, TextInput, Spinner, StatusBadge, EmptyState, Modal,
} from "../UI";
import {tokens} from "../../theme/tokens";

// Corporate approval tone (pending neutral, approved success, rejected danger).
const approvalTone = (s) =>
    s === "approved" ? "success" : s === "rejected" ? "danger" : "neutral";

// Invoice status tone.
const invoiceTone = (s) => {
    const v = (s || "").toUpperCase();
    if (v === "PAID") return "success";
    if (v === "UNPAID") return "danger";
    return "warn"; // PARTIALLY PAID, etc.
};

const kes = (n) => `KES ${(Number(n) || 0).toFixed(2)}`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    gap: ${tokens.spacing[4]};
    padding: ${tokens.spacing[3]} 0;
    border-bottom: 1px solid ${tokens.colors.divider};
    font-size: ${tokens.typography.size.base};
    &:last-child { border-bottom: 0; }
    .k { color: ${tokens.colors.muted}; }
    .v { color: ${tokens.colors.heading}; font-weight: ${tokens.typography.weight.semibold}; text-align: right; word-break: break-word; }
`;

const LedgerList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 320px;
    overflow: auto;
    li {
        padding: ${tokens.spacing[3]} 0;
        border-bottom: 1px solid ${tokens.colors.divider};
        font-size: ${tokens.typography.size.sm};
        color: ${tokens.colors.body};
        display: flex;
        justify-content: space-between;
        gap: ${tokens.spacing[3]};
    }
    li:last-child { border-bottom: 0; }
`;

const Field = ({label, value}) => (
    <Row><span className="k">{label}</span><span className="v">{value === 0 ? "0" : (value || "—")}</span></Row>
);

const BusinessDetails = props => {
    let history = useHistory();
    const [globalState, globalActions] = useGlobal();
    const [isLoading, setIsLoading] = useState(false);
    const [item, setItem] = useState({name: ''});
    const [tab, setTab] = useState("details");

    const [requestsList, setRequestList] = useState([]);
    const [departmentList, setDepartmentList] = useState([]);
    const [accountList, setAccountList] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [documents, setDocuments] = useState([]);

    const [walletLedger, setWalletLedger] = useState([]);
    const [walletAmountKES, setWalletAmountKES] = useState('');

    // Developer API — keys + webhook health for this business.
    const [apiKeys, setApiKeys] = useState([]);
    const [webhookHealth, setWebhookHealth] = useState([]);

    // Record-payment modal (collects the payment amount; the audit reason is then
    // auto-prompted by postData on the audited invoice endpoint).
    const [paymentInvoice, setPaymentInvoice] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');

    const {id} = props.match.params;

    // -------- loaders --------
    const load = useCallback(() => {
        let data = new FormData();
        data.append("id", id);
        setIsLoading(true);
        postData(Endpoints.BUSINESS_DETAIL, data)
            .then(res => res.json())
            .then(res => {
                setIsLoading(false);
                if (res.success === 1) {
                    setItem(res.data);
                    updateDocumentTitle(res.data.name);
                }
            })
            .catch((error) => { setIsLoading(false); console.log(error); });
    }, [id]);

    const loadRequests = useCallback(() => {
        let data = new FormData();
        data.append("business_id", id);
        postData(Endpoints.REQUEST_LIST_USER, data)
            .then(res => res.json())
            .then(res => setRequestList((res && res.data) || []))
            .catch((error) => console.log(error));
    }, [id]);

    const loadDepartments = useCallback(() => {
        let data = new FormData();
        data.append("business_id", id);
        postData(Endpoints.DEPARTMENT_LIST, data)
            .then(res => res.json())
            .then(res => setDepartmentList((res && res.data) || []))
            .catch((error) => console.log(error));
    }, [id]);

    const loadAccounts = useCallback(() => {
        let data = new FormData();
        data.append("business_id", id);
        postData(Endpoints.CHILD_ACCOUNT_LIST, data)
            .then(res => res.json())
            .then(res => setAccountList((res && res.data) || []))
            .catch((error) => console.log(error));
    }, [id]);

    const loadInvoices = useCallback(() => {
        let data = new FormData();
        data.append("business_id", id);
        postData(Endpoints.INVOICE_LIST_ADMIN, data)
            .then(res => res.json())
            .then(res => setInvoices((res && res.data) || []))
            .catch((error) => console.log(error));
    }, [id]);

    const loadDocuments = useCallback(() => {
        let data = new FormData();
        data.append("business_id", id);
        postData(Endpoints.BUSINESS_DOCUMENTS_ADMIN, data)
            .then(res => res.json())
            .then(res => setDocuments((res && res.data) || []))
            .catch((error) => console.log(error));
    }, [id]);

    const loadWalletLedger = useCallback(() => {
        if (!id) return;
        fetchData(Endpoints.BUSINESS_LEDGER(id))
            .then((r) => r.json())
            .then((res) => setWalletLedger((res && res.data) || []))
            .catch((err) => console.log(err));
    }, [id]);

    const loadApiKeys = useCallback(() => {
        let data = new FormData();
        data.append("business_id", id);
        postData(Endpoints.API_KEYS_LIST_ADMIN, data)
            .then(res => res.json())
            .then(res => setApiKeys((res && res.data) || []))
            .catch((error) => console.log(error));
    }, [id]);

    const loadWebhookHealth = useCallback(() => {
        let data = new FormData();
        data.append("business_id", id);
        postData(Endpoints.API_WEBHOOKS_ADMIN, data)
            .then(res => res.json())
            .then(res => setWebhookHealth((res && res.data) || []))
            .catch((error) => console.log(error));
    }, [id]);

    useEffect(() => {
        load();
        loadRequests();
        loadDepartments();
        loadAccounts();
        loadInvoices();
        loadDocuments();
        loadWalletLedger();
        loadApiKeys();
        loadWebhookHealth();
    }, [load, loadRequests, loadDepartments, loadAccounts, loadInvoices, loadDocuments, loadWalletLedger, loadApiKeys, loadWebhookHealth]);

    // -------- actions (audit reason auto-prompted by postData) --------
    // revokeApiKey hits the audited revoke endpoint; postData auto-prompts for
    // the required audit reason and retries.
    const revokeApiKey = (row) => {
        let data = new FormData();
        data.append("id", row.id);
        postData(Endpoints.API_KEY_REVOKE_ADMIN, data)
            .then(res => res.json())
            .then(res => {
                if (res.success === 1) {
                    Notify("API key revoked", SUCCESS);
                    loadApiKeys();
                    loadWebhookHealth();
                } else if (res && res.message) {
                    Notify(res.message, ERROR);
                }
            })
            .catch((error) => console.log(error));
    };

    const decide = (endpoint) => {
        let data = new FormData();
        data.append("id", id);
        postData(endpoint, data)
            .then(res => res.json())
            .then(res => {
                if (res.success === 1 && res.data) {
                    setItem(res.data);
                } else if (res && res.message) {
                    Notify(res.message, ERROR);
                }
            })
            .catch((error) => console.log(error));
    };

    const updateStatus = (number) => {
        let data = new FormData();
        data.append("id", id);
        data.append("status", number);
        postData(Endpoints.BUSINESS_UPDATE_STATUS, data)
            .then(res => res.json())
            .then(res => { if (res.success === 1) load(); })
            .catch((error) => console.log(error));
    };

    const submitWalletAction = (kind) => {
        const cents = Math.round(parseFloat(walletAmountKES) * 100);
        if (!Number.isFinite(cents) || cents <= 0) return;
        const fd = new FormData();
        fd.append('amount_cents', String(cents));
        const endpoint = kind === 'topup'
            ? Endpoints.BUSINESS_WALLET_TOPUP(id)
            : Endpoints.BUSINESS_WALLET_DEBIT(id);
        postData(endpoint, fd)
            .then(() => {
                setWalletAmountKES('');
                loadWalletLedger();
                load();
            })
            .catch((err) => console.log(err));
    };

    const markInvoicePaid = (invId) => {
        const fd = new FormData();
        fd.append('id', invId);
        postData(Endpoints.INVOICE_UPDATE_STATUS_ADMIN, fd)
            .then(res => res.json())
            .then(() => loadInvoices())
            .catch((err) => console.log(err));
    };

    const generateInvoice = () => {
        const fd = new FormData();
        fd.append('business_id', id);
        setIsLoading(true);
        postData(Endpoints.INVOICE_GENERATE_ADMIN, fd)
            .then(res => res.json())
            .then(res => {
                setIsLoading(false);
                Notify(res.success === 1 ? (res.data || "Invoices generated") : (res.message || res.error || "Failed"),
                    res.success === 1 ? SUCCESS : ERROR);
                if (res.success === 1) loadInvoices();
            })
            .catch((err) => { setIsLoading(false); console.log(err); });
    };

    const submitPayment = () => {
        if (!paymentInvoice) return;
        const amount = parseFloat(paymentAmount);
        if (!Number.isFinite(amount) || amount <= 0) return;
        const fd = new FormData();
        fd.append('id', paymentInvoice.id);
        fd.append('amount', String(amount));
        postData(Endpoints.INVOICE_UPDATE_PAYMENT_ADMIN, fd)
            .then(res => res.json())
            .then(() => {
                setPaymentInvoice(null);
                setPaymentAmount('');
                loadInvoices();
            })
            .catch((err) => console.log(err));
    };

    // -------- columns --------
    const requestColumns = [
        {name: 'Code', grow: 1, cell: row => <Link to={"/request/" + row.id}>{row.request_code}</Link>},
        {name: 'Date', grow: 1, selector: row => row.date_string || row.pick_up_date_time_string},
        {name: 'Pick up', grow: 2, selector: 'pick_up_location', wrap: true},
        {name: 'Type', grow: 1, selector: 'delivery_type'},
        {name: 'Status', grow: 1, cell: row => <StatusBadge status={row.status}>{row.status}</StatusBadge>},
        {name: 'Fee', grow: 1, right: true, selector: row => row.fee},
    ];

    const departmentColumns = [
        {name: 'Name', grow: 2, cell: row => <Link to={"/department/" + row.id}>{row.name}</Link>},
        {name: 'Contact email', grow: 2, selector: 'contact_email'},
        {name: 'Contact phone', grow: 1, selector: 'contact_phone'},
        {name: 'Status', cell: row => row.active === 1
            ? <StatusBadge tone="success">Active</StatusBadge>
            : <StatusBadge tone="neutral">Inactive</StatusBadge>},
    ];

    const accountColumns = [
        {name: 'Name', grow: 2, cell: row => <Link to={"/account/" + row.id}>{row.name}</Link>},
        {name: 'Email', grow: 2, selector: 'email'},
        {name: 'Phone', grow: 1, selector: 'phone'},
        {name: 'Status', cell: row => row.active === 1
            ? <StatusBadge tone="success">Active</StatusBadge>
            : <StatusBadge tone="neutral">Inactive</StatusBadge>},
    ];

    const invoiceColumns = [
        {name: 'Invoice', grow: 1, cell: row => <span style={{fontWeight: 600}}>{row.invoice_code || `#${row.id}`}</span>},
        {name: 'Raised', grow: 1, selector: 'date_added_string'},
        {name: 'Amount', grow: 1, right: true, cell: row => kes(row.amount)},
        {name: 'Paid', grow: 1, right: true, cell: row => kes(row.amount_paid)},
        {name: 'Status', grow: 1, cell: row => <StatusBadge tone={invoiceTone(row.status)}>{row.status}</StatusBadge>},
        {name: 'Due', grow: 1, selector: 'due_date_string'},
        {
            name: '', grow: 2, right: true,
            cell: row => (
                <Guard permission={PERMS.FINANCIAL_WRITE}>
                    <div style={{display: 'flex', gap: tokens.spacing[2], justifyContent: 'flex-end'}}>
                        <Button size="sm" variant="outline" onClick={() => { setPaymentInvoice(row); setPaymentAmount(''); }}>
                            Record payment
                        </Button>
                        {(row.status || '').toUpperCase() !== 'PAID' &&
                        <Button size="sm" onClick={() => markInvoicePaid(row.id)}>Mark paid</Button>}
                    </div>
                </Guard>
            ),
        },
    ];

    const apiKeyColumns = [
        {name: 'Key id', grow: 2, cell: row => <span style={{fontWeight: 600}}>{row.key_prefix}</span>},
        {name: 'Mode', grow: 1, cell: row => <StatusBadge tone={row.mode === 'live' ? 'brand' : 'info'} size="sm">{(row.mode || '').toUpperCase()}</StatusBadge>},
        {name: 'Scope', grow: 1, selector: row => row.department_id ? 'Department' : 'Default'},
        {name: 'Status', grow: 1, cell: row => <StatusBadge tone={row.status === 'revoked' ? 'danger' : 'success'} size="sm">{row.status}</StatusBadge>},
        {
            name: '', grow: 1, right: true,
            cell: row => row.status !== 'revoked' && (
                <Guard permission={PERMS.FINANCIAL_WRITE}>
                    <Button size="sm" variant="danger" onClick={() => revokeApiKey(row)}>Revoke</Button>
                </Guard>
            ),
        },
    ];

    const approval = item.approval_status || 'pending';
    const hasPendingInvoice = invoices.some(i => (i.status || '').toUpperCase() !== 'PAID');

    return (
        <PageLayout
            title={item.name}
            actions={item.name !== '' ? (
                <>
                    {isLoading && <Spinner size={20}/>}
                    <StatusBadge tone={approvalTone(approval)}>{approval}</StatusBadge>
                    <Guard permission={PERMS.ADMIN_WRITE}>
                        <Button
                            onClick={() => decide(Endpoints.BUSINESS_APPROVE)}
                            disabled={approval === 'approved'}
                        >
                            Approve
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => decide(Endpoints.BUSINESS_REJECT)}
                            disabled={approval === 'rejected'}
                        >
                            Reject
                        </Button>
                    </Guard>
                    <Link to="/businesses">
                        <Button variant="outline">Back</Button>
                    </Link>
                </>
            ) : null}
        >
            {item.name !== '' &&
            <>
                {approval === 'rejected' && item.rejection_reason &&
                <Card tone="brand" style={{marginBottom: tokens.spacing[4], borderColor: tokens.colors.status.danger.border}}>
                    <strong style={{color: tokens.colors.status.danger.fg}}>Rejected.</strong>{' '}
                    <span style={{color: tokens.colors.body}}>{item.rejection_reason}</span>
                </Card>
                }

                {hasPendingInvoice &&
                <Card tone="muted" style={{marginBottom: tokens.spacing[4]}}>
                    <span style={{color: tokens.colors.status.warn.fg, fontWeight: 600}}>⚠ Pending invoice</span>{' '}
                    <span style={{color: tokens.colors.muted}}>— this business has one or more unpaid invoices.</span>
                </Card>
                }

                <Tabs
                    tabs={[
                        {key: "details", label: "Details"},
                        {key: "documents", label: "Documents"},
                        {key: "requests", label: "Requests"},
                        {key: "invoices", label: "Invoices"},
                        {key: "wallet", label: "Wallet"},
                        {key: "structure", label: "Departments & Accounts"},
                        {key: "rates", label: "Rates & Charges"},
                        {key: "api", label: "API"},
                    ]}
                    value={tab}
                    onChange={setTab}
                />

                {tab === "details" &&
                <Card style={{maxWidth: 720}}>
                    <Field label="Name" value={item.name}/>
                    <Field label="Approval status" value={<StatusBadge tone={approvalTone(approval)}>{approval}</StatusBadge>}/>
                    <Field label="Registration number" value={item.registration_number}/>
                    <Field label="KRA PIN" value={item.pin}/>
                    <Field label="Business type" value={item.business_type}/>
                    <Field label="Category" value={item.category}/>
                    <Field label="Contact email" value={item.contact_email}/>
                    <Field label="Contact phone" value={item.contact_phone}/>
                    <Field label="Physical address" value={item.physical_address}/>
                    <Field label="Postal address" value={item.postal_address}/>
                    <Field label="Country" value={item.country}/>
                    <Field label="M-Pesa service fee" value={item.mpesa_service_fee_string}/>
                    <Field label="Card service fee" value={item.card_service_fee_string}/>
                    <Field label="Auto-invoice threshold" value={kes((item.auto_invoice_threshold_cents || 0) / 100)}/>
                    <Field label="This month's requests" value={item.this_months_requests}/>
                    <Field label="This month's bill" value={kes(item.this_month)}/>
                    <Field label="Client status" value={item.active === 1 ? "Active" : "Inactive"}/>
                    <Field label="Admin status" value={item.admin_active === 1 ? "Active" : "Inactive"}/>
                    <Field label="Registered on" value={item.date_added_string}/>

                    <Guard permission={PERMS.ADMIN_WRITE}>
                        <div style={{marginTop: tokens.spacing[4], display: 'flex', gap: tokens.spacing[2]}}>
                            <Button
                                variant={item.admin_active === 1 ? "danger" : "primary"}
                                onClick={() => updateStatus(item.admin_active === 1 ? 0 : 1)}
                            >
                                {item.admin_active === 1 ? "Deactivate account" : "Activate account"}
                            </Button>
                        </div>
                    </Guard>
                </Card>
                }

                {tab === "documents" &&
                <Card style={{maxWidth: 720}}>
                    <CardTitleBar title="KYC & supporting documents"/>
                    <CardBody>
                        {(item.supporting_document || documents.length > 0) ? (
                            <div style={{display: 'flex', flexDirection: 'column', gap: tokens.spacing[3]}}>
                                {item.supporting_document &&
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacing[3]}}>
                                    <span style={{color: tokens.colors.body}}>Supporting document</span>
                                    <a href={Endpoints.SERVER_HOST + Endpoints.FILE_URL + item.supporting_document}
                                       target="_blank" rel="noreferrer">
                                        <Button variant="outline" size="sm">View</Button>
                                    </a>
                                </div>
                                }
                                {documents.map(doc => (
                                    <div key={doc.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacing[3]}}>
                                        <span style={{color: tokens.colors.body}}>{doc.name || `Document #${doc.id}`}</span>
                                        <a href={Endpoints.SERVER_HOST + Endpoints.FILE_URL + doc.file_url}
                                           target="_blank" rel="noreferrer">
                                            <Button variant="outline" size="sm">View</Button>
                                        </a>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <EmptyState title="No documents" description="This business has not uploaded any supporting documents."/>
                        )}
                    </CardBody>
                </Card>
                }

                {tab === "requests" &&
                <Card padded={false}>
                    <DataTable
                        columns={requestColumns}
                        data={requestsList}
                        pointerOnHover={true}
                        noHeader={true}
                        onRowClicked={e => history.push('/request/' + e.id)}
                    />
                </Card>
                }

                {tab === "invoices" &&
                <>
                    <Guard permission={PERMS.FINANCIAL_WRITE}>
                        <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: tokens.spacing[3]}}>
                            <Button onClick={generateInvoice}>Generate invoice</Button>
                        </div>
                    </Guard>
                    <Card padded={false}>
                        <DataTable
                            columns={invoiceColumns}
                            data={invoices}
                            noHeader={true}
                            noDataComponent={
                                <EmptyState
                                    title="No invoices yet"
                                    description="No invoices yet — generate one or wait for the monthly billing run."
                                />
                            }
                        />
                    </Card>
                </>
                }

                {tab === "api" &&
                <>
                    <Card padded={false} style={{marginBottom: tokens.spacing[4]}}>
                        <CardTitleBar title="API keys"/>
                        <DataTable
                            columns={apiKeyColumns}
                            data={apiKeys}
                            noHeader={true}
                            noDataComponent={
                                <EmptyState
                                    title="No API keys"
                                    description="This business has not created any developer API keys yet."
                                />
                            }
                        />
                    </Card>

                    <Card>
                        <CardTitleBar title="Webhook health"/>
                        <CardBody>
                            {webhookHealth.length === 0
                                ? <EmptyState title="No webhook activity" description="Webhook deliveries appear here once the business receives API events."/>
                                : webhookHealth.map((h, idx) => (
                                    <div key={h.key_prefix || idx} style={{marginBottom: tokens.spacing[4], paddingBottom: tokens.spacing[3], borderBottom: `1px solid ${tokens.colors.border || '#eee'}`}}>
                                        <div style={{display: "flex", alignItems: "center", gap: tokens.spacing[2], flexWrap: "wrap"}}>
                                            <span style={{fontWeight: 600, color: tokens.colors.heading}}>{h.key_prefix}</span>
                                            <StatusBadge tone={h.mode === 'live' ? 'brand' : 'info'} size="sm">{(h.mode || '').toUpperCase()}</StatusBadge>
                                            <StatusBadge tone={(h.recent_failures || 0) > 0 ? 'danger' : 'success'} size="sm">
                                                {(h.recent_failures || 0)} failing
                                            </StatusBadge>
                                        </div>
                                        <p style={{color: tokens.colors.muted, fontSize: tokens.typography.size.sm, marginTop: 4}}>
                                            Callback: {h.callback_url || '— not set —'}
                                        </p>
                                        {(h.recent || []).length > 0 &&
                                        <LedgerList>
                                            {(h.recent || []).slice(0, 8).map((d) => (
                                                <li key={d.id}>
                                                    <span>
                                                        <StatusBadge tone={d.delivered ? 'success' : 'danger'} size="sm">
                                                            {d.delivered ? 'delivered' : 'failed'}
                                                        </StatusBadge>
                                                        <span style={{marginLeft: tokens.spacing[2]}}>{d.event_type}</span>
                                                    </span>
                                                    <span style={{color: tokens.colors.faint, whiteSpace: 'nowrap'}}>
                                                        HTTP {d.last_status_code || 0} · {d.attempts || 0} try
                                                    </span>
                                                </li>
                                            ))}
                                        </LedgerList>}
                                    </div>
                                ))
                            }
                        </CardBody>
                    </Card>
                </>
                }

                {tab === "wallet" &&
                <Guard permission={PERMS.FINANCIAL_READ}>
                    <Card style={{maxWidth: 720}}>
                        <CardTitleBar title="Wallet"/>
                        <CardBody>
                            <div style={{fontSize: tokens.typography.size["2xl"], fontWeight: 800, color: tokens.colors.heading}}>
                                {kes((item.wallet_cents || 0) / 100)}
                            </div>
                            <p style={{color: tokens.colors.muted, fontSize: tokens.typography.size.sm, marginTop: 4}}>
                                Auto-invoice threshold: {kes((item.auto_invoice_threshold_cents || 0) / 100)}
                            </p>

                            <Guard permission={PERMS.FINANCIAL_WRITE}>
                                <div style={{marginTop: tokens.spacing[4], marginBottom: tokens.spacing[4]}}>
                                    <FormField label="Amount (KES)" htmlFor="walletAmount">
                                        <TextInput
                                            id="walletAmount"
                                            type="number" min="0" step="0.01"
                                            value={walletAmountKES}
                                            onChange={(e) => setWalletAmountKES(e.target.value)}
                                        />
                                    </FormField>
                                    <div style={{display: "flex", gap: tokens.spacing[2], alignItems: "center", flexWrap: "wrap", marginTop: tokens.spacing[2]}}>
                                        <Button disabled={!walletAmountKES} onClick={() => submitWalletAction('topup')}>Top up</Button>
                                        <Button variant="secondary" disabled={!walletAmountKES} onClick={() => submitWalletAction('debit')}>Debit</Button>
                                        <a
                                            href={`${Endpoints.SERVER_HOST}/api/v1/statement?entity_type=business&entity_id=${id}`}
                                            target="_blank" rel="noreferrer"
                                        >
                                            <Button variant="ghost">Download CSV statement</Button>
                                        </a>
                                    </div>
                                </div>
                            </Guard>

                            <h4 style={{color: tokens.colors.heading, margin: `${tokens.spacing[4]} 0 ${tokens.spacing[2]}`}}>Recent entries</h4>
                            {walletLedger.length === 0
                                ? <EmptyState title="No wallet activity" description="Top-ups and debits will appear here."/>
                                : <LedgerList>
                                    {walletLedger.map((e) => (
                                        <li key={e.id}>
                                            <span>
                                                <StatusBadge tone={e.direction === 'credit' ? 'success' : 'danger'} size="sm">{e.direction}</StatusBadge>
                                                <span style={{marginLeft: tokens.spacing[2]}}>{kes((e.amount_cents || 0) / 100)}</span>
                                                <span style={{color: tokens.colors.muted, marginLeft: tokens.spacing[2]}}>{e.reason || '—'}</span>
                                            </span>
                                            <span style={{color: tokens.colors.faint, whiteSpace: 'nowrap'}}>{new Date(e.created_at).toLocaleString()}</span>
                                        </li>
                                    ))}
                                </LedgerList>
                            }
                        </CardBody>
                    </Card>
                </Guard>
                }

                {tab === "structure" &&
                <>
                    <Card padded={false} style={{marginBottom: tokens.spacing[4]}}>
                        <CardTitleBar title="Departments"/>
                        <DataTable
                            columns={departmentColumns}
                            data={departmentList}
                            pointerOnHover={true}
                            noHeader={true}
                            onRowClicked={e => history.push('/department/' + e.id)}
                        />
                    </Card>
                    <Card padded={false}>
                        <CardTitleBar title="Billing accounts"/>
                        <DataTable
                            columns={accountColumns}
                            data={accountList}
                            pointerOnHover={true}
                            noHeader={true}
                            onRowClicked={e => history.push('/account/' + e.id)}
                        />
                    </Card>
                </>
                }

                {tab === "rates" &&
                <div className="row">
                    <div className="col-md-8">
                        <ListRatesComponent business={id}/>
                    </div>
                    <div className="col-md-4">
                        <AddRatesComponent/>
                    </div>
                </div>
                }
            </>
            }

            {paymentInvoice &&
            <Modal
                open={!!paymentInvoice}
                onClose={() => setPaymentInvoice(null)}
                title="Record a payment"
                subtitle={`Invoice ${paymentInvoice.invoice_code || paymentInvoice.id} — outstanding ${kes((paymentInvoice.amount || 0) - (paymentInvoice.amount_paid || 0))}`}
                actions={
                    <>
                        <Button variant="ghost" onClick={() => setPaymentInvoice(null)}>Cancel</Button>
                        <Button disabled={!paymentAmount} onClick={submitPayment}>Record payment</Button>
                    </>
                }
            >
                <FormField label="Payment amount (KES)" htmlFor="paymentAmount">
                    <TextInput
                        id="paymentAmount"
                        type="number" min="0" step="0.01"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        autoFocus
                    />
                </FormField>
            </Modal>
            }
        </PageLayout>
    )
};

export default BusinessDetails;
